import { database } from './core';
import { ref, query, orderByChild, limitToLast, get, onValue, off } from 'firebase/database';
import { FeedItem } from './feed.service';
import logger from '../../utils/logger';

// Public feed resource path
const PUBLIC_FEED_PATH = 'publicFeed';
const BATCH_SIZE = 10;

/**
 * Subscribes to public feed items
 * @param callback Function to call with feed items data
 * @param limit Maximum number of items to fetch
 * @param orderCriterion Criterion to order items by ('dateCreated' or 'likesCount')
 * @returns Unsubscribe function
 */
export const subscribeToPublicFeedItems = (
  callback: (feedItemsList: string[], feedItemsData: Record<string, any>) => void,
  limit: number = BATCH_SIZE,
  orderCriterion: 'dateCreated' | 'likesCount' = 'dateCreated'
): (() => void) => {
  try {
    if (!database) {
      logger.error('Firebase database not initialized');
      callback([], {});
      return () => {};
    }
    
    logger.info(`Subscribing to public feed with ${limit} items ordered by ${orderCriterion}`);
    
    // Create ref to public feed
    const publicFeedRef = ref(database, PUBLIC_FEED_PATH);
    
    // Create query ordered by specified criterion
    const feedQuery = query(
      publicFeedRef,
      orderByChild(orderCriterion),
      limitToLast(limit)
    );
    
    // Remove any existing listeners
    off(feedQuery);
    
    // Register listener and capture the unsubscribe function
    const unsubscribe = onValue(feedQuery, (snapshot) => {
      const feedItemsList: string[] = [];

      // If no feed exists, return empty arrays
      if (!snapshot.exists()) {
        logger.info('No public feed found');
        callback([], {});
        return;
      }

      logger.info(`Public feed data found, processing ${snapshot.size} items`);

      // Iterate through items (Firebase SDK method)
      snapshot.forEach((itemSnap: any) => {
        feedItemsList.push(itemSnap.key);
      });

      // Reverse items order (newest first)
      feedItemsList.reverse();

      logger.info(`Returning ${feedItemsList.length} public feed items`);
      callback(feedItemsList, snapshot.val());
    });

    // Return the captured unsubscribe function
    return unsubscribe;
  } catch (error) {
    logger.error('Exception in subscribeToPublicFeedItems:', error);
    callback([], {});
    return () => {};
  }
};

/**
 * Gets public feed with Promise API (for older code compatibility)
 * @param limit Maximum number of items to fetch
 * @returns Promise with array of feed items
 */
export const getPublicFeed = async (limit: number = BATCH_SIZE): Promise<FeedItem[]> => {
  if (!database) {
    logger.error('Firebase database not initialized');
    return [];
  }
  
  try {
    logger.info(`Getting public feed with limit ${limit}`);
    
    // Create ref to public feed
    const publicFeedRef = ref(database, PUBLIC_FEED_PATH);
    
    // Create query
    const feedQuery = query(
      publicFeedRef,
      orderByChild('dateCreated'),
      limitToLast(limit)
    );
    
    // Get data
    const snapshot = await get(feedQuery);
    
    // If no feed exists, return empty array
    if (!snapshot.exists()) {
      logger.info('No public feed found');
      return [];
    }
    
    logger.info(`Public feed data found, processing ${snapshot.size} items`);
    
    // Process feed items
    const feedItems: FeedItem[] = [];
    
    snapshot.forEach((childSnapshot) => {
      const feedItem = childSnapshot.val();
      feedItems.push({
        id: childSnapshot.key!,
        ...feedItem
      });
    });
    
    // Sort by date (newest first)
    feedItems.sort((a, b) => b.dateCreated - a.dateCreated);
    
    logger.info(`Returning ${feedItems.length} public feed items`);
    return feedItems;
  } catch (error) {
    logger.error('Error in getPublicFeed:', error);
    return [];
  }
};

const publicFeedService = {
  subscribeToPublicFeedItems,
  getPublicFeed
};

export default publicFeedService;